import { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Loader2 } from 'lucide-react';
import { getCurrentThread, streamMessage } from '../../services/aiTutor';
import LatexRenderer from './LatexRenderer';

export default function ChatWindow({ onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [threadId, setThreadId] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        getCurrentThread()
            .then((res) => {
                setThreadId(res.data.thread.id);
                const loaded = (res.data.messages || []).map((m) => ({
                    id: m.id,
                    role: m.role,
                    content: m.content,
                }));
                setMessages(loaded);
            })
            .catch(() => {})
            .finally(() => setInitialized(true));
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || !threadId || loading) return;

        const userText = input.trim();
        setInput('');
        setLoading(true);

        const userMessage = { role: 'user', content: userText, id: Date.now() };
        setMessages((prev) => [...prev, userMessage, { role: 'assistant', content: '', id: 'streaming' }]);

        let assistantContent = '';
        let finished = false;

        await streamMessage(threadId, userText, {
            onChunk: (chunk) => {
                if (finished) return;
                assistantContent += chunk;
                setMessages((prev) => {
                    const withoutStreaming = prev.filter((m) => m.id !== 'streaming');
                    return [...withoutStreaming, { role: 'assistant', content: assistantContent, id: 'streaming' }];
                });
            },
            onDone: () => {
                if (finished) return;
                finished = true;
                setMessages((prev) => {
                    const withoutStreaming = prev.filter((m) => m.id !== 'streaming');
                    return [...withoutStreaming, { role: 'assistant', content: assistantContent, id: Date.now() + 1 }];
                });
                setLoading(false);
            },
            onError: (error) => {
                if (finished) return;
                finished = true;
                setMessages((prev) => [
                    ...prev.filter((m) => m.id !== 'streaming'),
                    { role: 'assistant', content: `Xatolik: ${error}`, id: Date.now() + 1 },
                ]);
                setLoading(false);
            },
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="flex flex-col w-96 h-[28rem] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="flex items-center gap-2">
                    <Bot className="w-5 h-5" />
                    <span className="font-semibold text-sm">AI Tutor</span>
                </div>
                <button onClick={onClose} className="hover:bg-white/20 rounded-lg p-1 transition">
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-950">
                {!initialized && (
                    <div className="flex justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    </div>
                )}

                {messages.length === 0 && initialized && (
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                        Salom! Men sizga "Informatikaning nazariy asoslari" fani bo'yicha qadamma-qadam yordam beraman.
                    </div>
                )}

                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                    >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                            msg.role === 'user'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                        }`}>
                            {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                        </div>
                        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                            msg.role === 'user'
                                ? 'bg-blue-600 text-white rounded-br-none'
                                : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-none shadow-sm'
                        }`}>
                            {msg.role === 'assistant' ? (
                                <LatexRenderer text={msg.content} />
                            ) : (
                                <span className="whitespace-pre-wrap">{msg.content}</span>
                            )}
                        </div>
                    </div>
                ))}
                <div ref={bottomRef} />
            </div>

            <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-end gap-2">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        placeholder="Savolingizni yozing..."
                        className="flex-1 resize-none max-h-24 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 dark:text-gray-100 placeholder-gray-400"
                    />
                    <button
                        onClick={handleSend}
                        disabled={loading || !input.trim()}
                        className="p-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        </div>
    );
}
